import { z } from 'zod';
import { oltTr069ProfileSchema } from './tr069.js';
import { oltImageSchema } from './olt-image.js';
import { OLT_CAPABILITY_IDS } from './olt-family.js';
import { onuManagementStateSchema } from './onu-management.js';

export const oltVendorSchema = z.enum(['zte', 'huawei']);
export type OltVendor = z.infer<typeof oltVendorSchema>;

export const oltStatusSchema = z.enum(['up', 'down', 'unknown']);
export type OltStatus = z.infer<typeof oltStatusSchema>;

export const cliTransportSchema = z.enum(['ssh', 'telnet']);
export type CliTransport = z.infer<typeof cliTransportSchema>;

export const snmpVersionSchema = z.enum(['1', '2c', '3']);
export type SnmpVersion = z.infer<typeof snmpVersionSchema>;

/**
 * What the platform knows about a chassis's SSH host key.
 *
 * `state` is the operator-facing summary:
 *  - `pinned`    — a key is held and a person has compared it with the chassis
 *  - `learned`   — held, but taken on trust the first time we connected. This
 *                  is where every OLT registered before pinning shipped lands,
 *                  and it is honest about it rather than pretending to be
 *                  verified.
 *  - `mismatch`  — the chassis is presenting a different key and sessions are
 *                  being refused. A firmware upgrade does this legitimately.
 *  - `unpinned`  — nothing held yet; the next connection learns one.
 *  - `telnet`    — this OLT is managed over telnet, which has no host key to
 *                  pin at all. Not a gap in the pinning: the credential goes
 *                  over that wire in clear regardless.
 */
export const hostKeyStateSchema = z.enum([
  'pinned',
  'learned',
  'mismatch',
  'unpinned',
  'telnet',
]);
export type HostKeyState = z.infer<typeof hostKeyStateSchema>;

/** The algorithms a chassis actually agreed to on its last SSH handshake. */
export const negotiatedAlgorithmsSchema = z.object({
  kex: z.string(),
  cipher: z.string(),
  serverHostKey: z.string(),
});
export type NegotiatedSshAlgorithms = z.infer<typeof negotiatedAlgorithmsSchema>;

/**
 * The legacy entries, named once so the API and the UI cannot disagree about
 * what "legacy" means. `ssh-rsa` is the SHA-1 signature, not the RSA key — a
 * chassis that negotiated `rsa-sha2-256` is presenting the same key without
 * needing it.
 */
export const LEGACY_SSH_ALGORITHMS = [
  'diffie-hellman-group14-sha1',
  'diffie-hellman-group1-sha1',
  'aes128-cbc',
  '3des-cbc',
  'ssh-rsa',
];

/**
 * Whether a chassis actually used a legacy algorithm on its last connection.
 *
 * This is the safe way to answer "can I turn legacy off for this OLT?" — the
 * platform offers modern algorithms first, so whatever was negotiated is the
 * best that chassis can do. False here means turning the flag off changes
 * nothing on the wire; true means it would take SSH to that chassis away.
 */
export function usesLegacyAlgorithms(negotiated: NegotiatedSshAlgorithms | null): boolean {
  if (!negotiated) return false;
  return [negotiated.kex, negotiated.cipher, negotiated.serverHostKey].some((a) =>
    LEGACY_SSH_ALGORITHMS.includes(a),
  );
}

export const oltHostKeySchema = z.object({
  state: hostKeyStateSchema,
  /** OpenSSH form (`SHA256:…`) — comparable with `ssh-keygen -lf` on the chassis. */
  fingerprint: z.string().nullable(),
  keyType: z.string().nullable(),
  learnedAt: z.number().int().nullable(),
  lastSeenAt: z.number().int().nullable(),
  /** The key being presented and refused, waiting for an operator to accept it. */
  pendingFingerprint: z.string().nullable(),
  pendingKeyType: z.string().nullable(),
  pendingAt: z.number().int().nullable(),
  /** 'enforce' refuses a changed key; 'warn' records it and connects anyway. */
  policy: z.enum(['enforce', 'warn']),
  /** Whether this chassis is offered the legacy KEX/cipher/host-key algorithms. */
  legacyAlgorithms: z.boolean(),
  /** What it agreed to last time, or null if it has not connected since this shipped. */
  negotiated: negotiatedAlgorithmsSchema.nullable(),
  /**
   * Whether the last connection actually used one of the legacy algorithms.
   * False with a `negotiated` present is the evidence that the flag can be
   * turned off for this chassis without taking SSH away from it.
   */
  legacyInUse: z.boolean(),
});
export type OltHostKey = z.infer<typeof oltHostKeySchema>;

/** Accepting a changed key names it, so "accept" cannot become "trust anything". */
export const acceptHostKeySchema = z.object({ fingerprint: z.string().min(10).max(120) });

/**
 * Take a chassis or an ONU out of service, or put it back.
 *
 * The reason is optional and free text on purpose: it is for the next person
 * who wonders why something stopped reporting, and the set of reasons is not
 * knowable in advance. Enabling clears it, because a stale reason on something
 * back in service is worse than none.
 */
export const setDisabledSchema = z.object({
  disabled: z.boolean(),
  reason: z.string().max(300).nullable().optional(),
});
export type SetDisabledRequest = z.infer<typeof setDisabledSchema>;
export const hostKeySettingsSchema = z.object({
  policy: z.enum(['enforce', 'warn']).optional(),
  legacyAlgorithms: z.boolean().optional(),
});

/** PON flavours a port can run. */
export const ponTypeSchema = z.enum(['gpon', 'epon', 'xgpon', 'xgspon', 'unknown']);
export type PonType = z.infer<typeof ponTypeSchema>;

export const oltSchema = z.object({
  id: z.string(),
  /** Stable, human-friendly number used in URLs (/olts/1); null only until backfilled. */
  number: z.number().int().nullable(),
  orgId: z.string(),
  orgSlug: z.string().nullable().optional(),
  siteId: z.string().nullable(),
  siteName: z.string().nullable().optional(),
  name: z.string(),
  vendor: oltVendorSchema,
  model: z.string().nullable(),
  mgmtIp: z.string(),
  snmpVersion: snmpVersionSchema,
  snmpPort: z.number().int(),
  cliTransport: cliTransportSchema,
  cliPort: z.number().int(),
  cliUsername: z.string().nullable(),
  status: oltStatusSchema,
  softwareVersion: z.string().nullable(),
  hardwareVersion: z.string().nullable(),
  uptimeSeconds: z.number().int().nullable(),
  ponPortCount: z.number().int(),
  onuCount: z.number().int(),
  onuOnlineCount: z.number().int(),
  lastPolledAt: z.number().int().nullable(),
  /**
   * Taken out of service by a person. `status` beside it is what the last poll
   * found; this is a decision, and often about a chassis that answers fine.
   */
  disabledAt: z.number().int().nullable().optional(),
  disabledReason: z.string().nullable().optional(),
  lastError: z.string().nullable(),
  /**
   * The time servers this OLT should be asking, as SimpleACS holds them.
   *
   * Intent rather than a reading — what the chassis is actually configured
   * with is read from the chassis, and the Time card shows both, because a
   * chassis that lost its NTP config still reports a plausible-looking clock
   * right up until it doesn't.
   */
  ntpServers: z.array(z.string()).nullable(),
  /** ZTE poll-interval exponent, Huawei sync-interval seconds. Different units. */
  ntpInterval: z.number().int().nullable(),
  /**
   * The IANA zone this chassis is in, as SimpleACS holds it — and, separately,
   * whether that zone was ever written to the chassis.
   *
   * Two fields rather than one because they answer different questions and are
   * routinely different. The zone HELD is what every per-OLT schedule is
   * resolved in: a nightly backup at 02:00 on a chassis in Accra runs at 02:00
   * Accra time even when the server is in Nairobi. Whether the CHASSIS was
   * ever put in that zone is a separate act on the Time card. Optional so an
   * older client keeps parsing.
   */
  timezone: z.string().nullable().optional(),
  timezoneAppliedAt: z.number().int().nullable().optional(),
  /** SSH host-key pinning state. Optional so an older client keeps parsing. */
  hostKey: oltHostKeySchema.optional(),
  /**
   * The photograph an operator uploaded of this chassis, or null when nobody
   * has — which is most OLTs, and is not a gap: the page falls back to the
   * bundled drawing for the model's family. The bytes are at
   * GET /olts/:id/image, behind the same tenancy as the OLT itself. Optional
   * so an older client keeps parsing.
   */
  image: oltImageSchema.nullable().optional(),
  /**
   * Whether this chassis may nag on the OLT list.
   *
   * Covers the unseen-event COUNT in the Events column and the warning
   * triangle beside the name, and nothing else. Events keep being recorded for
   * a silenced OLT and its own Events tab keeps listing them; genuine alarms
   * (unreachable chassis, failed backup, optical findings) travel through the
   * notification bell, which reads findings and has never read ONU events.
   *
   * Optional so an older client keeps parsing — and every reader must go
   * through `eventNotificationsOn` rather than testing this field directly,
   * because absent means ON.
   */
  eventNotifications: z.boolean().optional(),
  /**
   * What this chassis GENERATION refuses, resolved server-side, keyed by
   * `OltCapabilityId` with the operator-facing sentence as the value.
   *
   * Only the refused capabilities are present, so `{}` is the ordinary answer
   * and means "nothing here is gated". Resolved on the server from the one
   * family-gate table (`services/bulk/vendor-support.ts`) rather than shipped
   * as a matrix for the browser to index — the same shape as
   * `device.firewallSupported` — so a control can be disabled where it is
   * drawn, with the exact sentence the server would have refused with, and
   * no page needs the model regex or a copy of the table. Optional so an
   * older client keeps parsing; always sent.
   */
  refusals: z.record(z.enum(OLT_CAPABILITY_IDS), z.string()).optional(),
  createdAt: z.number().int(),
});
export type Olt = z.infer<typeof oltSchema>;

/**
 * The one place the "absent or null means ON" rule is spelled out.
 *
 * Only an explicit `false` silences an OLT. Everything else — `true`, `null`
 * from a row written before the column existed, `undefined` from an API that
 * predates it — is a chassis that has never been silenced, and answering
 * anything else would turn a missing value into a decision nobody made. The
 * failure this exists to prevent is a single `Boolean(olt.eventNotifications)`
 * quietly muting an entire fleet, which reports itself only as an absence of
 * nagging.
 */
export function eventNotificationsOn(olt: {
  eventNotifications?: boolean | null;
}): boolean {
  return olt.eventNotifications !== false;
}

/**
 * Time servers as an operator types them: one per line, or comma-separated.
 *
 * Accepting both is not indulgence — a list copied out of a running config
 * arrives one per line and a list from a ticket arrives comma-separated, and
 * rejecting either would mean retyping something already correct.
 */
export function parseNtpServers(input: string): string[] {
  return input
    .split(/[\s,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

export const createOltSchema = z.object({
  name: z.string().min(2).max(120),
  vendor: oltVendorSchema,
  mgmtIp: z.string().min(7).max(45),
  /** The gateway whose tunnel reaches this OLT, when it is not directly routable. */
  siteId: z.string().nullable().optional(),
  snmpVersion: snmpVersionSchema.default('2c'),
  snmpPort: z.coerce.number().int().min(1).max(65535).default(161),
  /** Read community for v1/v2c; ignored for v3. */
  snmpCommunity: z.string().max(200).optional(),
  /**
   * Time servers to point the chassis at once it is registered.
   *
   * Optional: an OLT with a working clock and no SimpleACS-managed NTP is a
   * normal state, and an empty list is left alone rather than treated as "no
   * servers", which would be a request to take away what it has.
   */
  ntpServers: z.array(z.string().min(1).max(255)).max(8).optional(),
  ntpInterval: z.coerce.number().int().min(4).max(1024).optional(),
  /**
   * The IANA zone the chassis sits in.
   *
   * Recorded here and used immediately — every per-OLT schedule (the nightly
   * config backup, the drift pass) is resolved against it. Writing it to the
   * CHASSIS is still a separate act on the Time card, and on Huawei still
   * needs an operator to accept an unconfirmed command form.
   */
  timezone: z.string().max(64).nullable().optional(),
  /** Read-write community, used only where a write is genuinely needed. */
  snmpCommunityRw: z.string().max(200).optional(),
  snmpV3: z
    .object({
      username: z.string().max(200),
      authProtocol: z.enum(['md5', 'sha']).optional(),
      authKey: z.string().max(200).optional(),
      privProtocol: z.enum(['des', 'aes']).optional(),
      privKey: z.string().max(200).optional(),
    })
    .optional(),
  cliTransport: cliTransportSchema.default('ssh'),
  cliPort: z.coerce.number().int().min(1).max(65535).default(22),
  cliUsername: z.string().max(120).optional(),
  cliPassword: z.string().max(200).optional(),
  /**
   * Whether this chassis may show an unseen-event count and a warning triangle
   * on the OLT list. Omitted means ON, which is what every OLT registered
   * before this existed does — the choice is to silence, never to opt in.
   *
   * Display only. A silenced OLT still records every ONU transition, still
   * lists them on its own Events tab, and still raises findings to the
   * notification bell when something is genuinely wrong with it.
   */
  eventNotifications: z.boolean().optional(),
  /** Superadmin may place the OLT in another organization. */
  orgId: z.string().optional(),
});
export type CreateOltRequest = z.infer<typeof createOltSchema>;

export const updateOltSchema = createOltSchema
  .partial()
  .omit({ orgId: true, vendor: true })
  .extend({ siteId: z.string().nullable().optional() });
export type UpdateOltRequest = z.infer<typeof updateOltSchema>;

export const ponPortSchema = z.object({
  id: z.string(),
  oltId: z.string(),
  /** Vendor notation, e.g. `1/3/15` on ZTE or `0/1/2` on Huawei. */
  port: z.string(),
  ponType: ponTypeSchema,
  description: z.string().nullable(),
  adminStatus: z.enum(['enabled', 'disabled', 'unknown']),
  operStatus: z.enum(['up', 'down', 'unknown']),
  onuCount: z.number().int(),
  onuOnlineCount: z.number().int(),
  /** Configured ONU limit, so the UI can draw a load bar. */
  maxOnus: z.number().int().nullable(),
  txPowerDbm: z.number().nullable(),
  averageRxPowerDbm: z.number().nullable(),
  updatedAt: z.number().int(),
});
export type PonPort = z.infer<typeof ponPortSchema>;

/**
 * What the CHASSIS says about an ONU it has registered.
 *
 * Every member here is produced by a real parser or OID mapper — that is the
 * entry requirement, and it is the reason `unconfigured` is not among them.
 * It sat in this enum producing nothing: no SNMP mapper, no ZTE or Huawei CLI
 * parser, and no row on the live fleet ever held it. What it did do was reach
 * the bulk-plan State filter, where picking it returned zero matches every
 * time — a filter that cannot be wrong because it can never be right.
 *
 * An ONU the chassis has NOT configured is a different thing entirely, and it
 * has its own home: the autofind/uncfg list behind `GET /api/olts/:id/unconfigured`,
 * which is about units the chassis can see but has not registered. Those units
 * have no row here to carry a state at all, which is exactly why this enum
 * could never express them.
 */
export const onuStateSchema = z.enum([
  'working',
  'offline',
  'los', // loss of signal
  'dying_gasp',
  'power_off',
  'unknown',
]);
export type OnuState = z.infer<typeof onuStateSchema>;

/**
 * Why the chassis says an ONU last went down — its own record, not a state.
 * One vocabulary over both vendors (ZTE zxAnGponSrvOnuLastOfflineReason, Huawei
 * hwGponDeviceOntControlLastDownCause); `other` keeps a code neither maps.
 */
export const onuDownReasonSchema = z.enum([
  'los', // loss of signal on the PON
  'losi', // loss of signal / burst for this ONU
  'lofi', // loss of frame for this ONU
  'sfi', // signal fail for this ONU
  'dying_gasp',
  'reboot',
  'deactivated',
  'reregister',
  'other',
]);
export type OnuDownReason = z.infer<typeof onuDownReasonSchema>;

/** The chassis's verdict on the ONU's delivered configuration (ZTE "Config state"). */
export const onuConfigStateSchema = z.enum(['success', 'fail', 'warning']);
export type OnuConfigState = z.infer<typeof onuConfigStateSchema>;

/**
 * How each ONU state reads in the UI (operator, 2026-09-02).
 *
 * Two deliberate choices. A `dying_gasp` and an explicit `power_off` are the
 * same fact to a subscriber — the unit lost mains — so both read "Power off"
 * rather than exposing the chassis's word for the last message it sent. And a
 * plain `offline` — the unit went dark WITHOUT a dying gasp — is labelled
 * "Possible LOS": with no power-off signal, a lost fibre is the likelier cause,
 * but the chassis did not say so, so the label does not claim it as fact the
 * way a definite `los` does.
 */
export const ONU_STATE_LABELS: Record<OnuState, string> = {
  working: 'Working',
  offline: 'Possible LOS',
  los: 'LOS',
  dying_gasp: 'Power off',
  power_off: 'Power off',
  unknown: 'Unknown',
};

export function onuStateLabel(state: OnuState): string {
  return ONU_STATE_LABELS[state] ?? state;
}

/**
 * The pill tone each state reads with — one answer for every list and card.
 *
 * A power cut (`dying_gasp`/`power_off`) is expected and self-resolving, so it
 * is neutral, not an alarm. A confirmed `los` is a fault (danger); a bare
 * `offline` — possible LOS — is worth a look but not yet confirmed (warn).
 */
export function onuStateTone(state: OnuState): 'success' | 'danger' | 'warn' | 'neutral' {
  switch (state) {
    case 'working':
      return 'success';
    case 'los':
      return 'danger';
    case 'offline':
      return 'warn';
    default:
      return 'neutral';
  }
}

/** What the platform knows about when an ONU was last up. */
export interface OnuUpEvidence {
  /** When this platform last saw it working. */
  lastSeenAt: number | null;
  /** The chassis's own record: when it last came online, and when it last went down (it was up until then). */
  lastOnlineAt?: number | null;
  lastOfflineAt?: number | null;
  /** When it arrived on this platform, and when it was (re)authorised. */
  createdAt?: number | null;
  authorizedAt?: number | null;
}

/**
 * The last moment an ONU is known to have been up — or, never seen up, the
 * moment it arrived here: the latest of when this platform last saw it
 * working, the chassis's own last-online and last-offline times, and when it
 * was created or authorised (operator, 2026-10-13: dormant only when it has
 * not been online for the whole threshold).
 *
 * It used to be `lastSeenAt ?? 0` — never seen up was "dark since 1970" — so
 * when the threshold fell to 30 days, the six lagos units that arrived on
 * 17 Sep already offline read dormant at once, and so would a freshly
 * authorised unit still waiting to be plugged in. The chassis's times matter
 * on a C650, which records them (a C300 does not): lagos knew exactly when
 * each of those units went down. One rule for the label and for the nightly
 * deletion (`dormant-cleanup.ts`), so the two never disagree.
 */
export function onuLastKnownUp(onu: OnuUpEvidence): number {
  return Math.max(
    onu.lastSeenAt ?? 0,
    onu.lastOnlineAt ?? 0,
    onu.lastOfflineAt ?? 0,
    onu.createdAt ?? 0,
    onu.authorizedAt ?? 0,
  );
}

/**
 * Whether an ONU has been down long enough to be "dormant" rather than merely
 * offline — likely a subscriber who left, not a fresh outage.
 *
 * Not a chassis state: it is derived from how long the unit has been dark
 * ({@link onuLastKnownUp}) against the operator's threshold (platform setting
 * `dormantAfterDays`, default 30 days). The API computes it — it holds the
 * threshold and the evidence — and every surface reads the resulting boolean
 * through the two helpers below, so a long-gone unit reads the same in the
 * list, on its card, and on the dashboard.
 */
export function onuDormant(
  state: OnuState,
  onu: OnuUpEvidence,
  dormantAfterMs: number,
  now: number = Date.now(),
): boolean {
  if (state === 'working') return false;
  return now - onuLastKnownUp(onu) >= dormantAfterMs;
}

/**
 * The state an ONU reads with when its chassis cannot be asked.
 *
 * `onus.state` is whatever the last sweep found, and a sweep needs the OLT.
 * When the OLT is DOWN (or taken out of service by a person) the row keeps
 * saying "working" indefinitely — HWTC36699C0C read Working for a day after
 * its chassis was unplugged and carried off (operator, 2026-09-09). Nobody has
 * looked, so the honest word is Unknown; the last-seen time beside it says
 * when somebody last did. Every list, card and count reads through this, so a
 * dead chassis's subscribers never count as watched.
 */
export function onuStateOnOlt(
  state: OnuState,
  olt: { status: string; disabledAt?: number | null } | null | undefined,
): OnuState {
  if (!olt) return state;
  return olt.status === 'down' || olt.disabledAt ? 'unknown' : state;
}

/** The label an ONU reads with once `dormant` is folded over its raw state. */
export function onuHealthLabel(onu: { state: OnuState; dormant?: boolean | null }): string {
  return onu.dormant ? 'Dormant' : onuStateLabel(onu.state);
}

/**
 * The pill tone with `dormant` folded in — neutral (grey), deliberately not the
 * warn/orange of a live outage: a dormant unit needs no attention, it is the
 * record of one that left.
 */
export function onuHealthTone(onu: {
  state: OnuState;
  dormant?: boolean | null;
}): 'success' | 'danger' | 'warn' | 'neutral' {
  return onu.dormant ? 'neutral' : onuStateTone(onu.state);
}

export const onuSchema = z.object({
  id: z.string(),
  orgId: z.string().nullable(),
  oltId: z.string(),
  oltName: z.string().nullable().optional(),
  ponPort: z.string(),
  onuIndex: z.string(),
  /** Vendor identifier, e.g. `gpon-onu_1/3/15:13`. */
  interfaceName: z.string().nullable(),
  serial: z.string(),
  name: z.string().nullable(),
  onuType: z.string().nullable(),
  state: onuStateSchema,
  rxPowerDbm: z.number().nullable(),
  txPowerDbm: z.number().nullable(),
  oltRxPowerDbm: z.number().nullable(),
  distanceM: z.number().int().nullable(),
  /** Optical-module DDM: transceiver temperature °C, supply voltage V, laser bias mA. */
  temperatureC: z.number().nullable().optional(),
  voltageV: z.number().nullable().optional(),
  biasMa: z.number().nullable().optional(),
  lineProfile: z.string().nullable(),
  serviceProfile: z.string().nullable(),
  /** Stable, human-friendly number used in URLs (/onus/1); null until backfilled. */
  number: z.number().int().nullable().optional(),
  /** Linked TR-069 device, matched on serial. */
  deviceId: z.string().nullable(),
  /** Physical placement, when the ONU is linked to a device the topology places. */
  zoneName: z.string().nullable().optional(),
  /** The splitter the ONU hangs off directly, not the chain above it. */
  splitterName: z.string().nullable().optional(),
  /** The same placement by id, for anything offering to change it. */
  zoneId: z.string().nullable().optional(),
  splitterId: z.string().nullable().optional(),
  splitterPort: z.number().int().nullable().optional(),
  /**
   * Free text a human wrote about this subscriber. Platform-only: no chassis
   * reports it and no sweep writes it, and saving one sends nothing to an OLT.
   *
   * Present because SmartOLT has held a note per ONU for the life of this
   * fleet. The import fills it when SimpleACS holds none and reports a conflict
   * rather than overwriting one, so a note edited by hand is never re-imported
   * over.
   */
  notes: z.string().nullable().optional(),
  /**
   * The identifier somebody else's software already uses for this subscriber.
   *
   * Captured from SmartOLT at import time, where — and only where — it differs
   * from the serial. Null is the normal case and means "this ONU answers to
   * its serial", which is exactly what SmartOLT's default means. A value here
   * is one a person deliberately set, usually a billing system's customer id,
   * and it is what a SmartOLT-compatible API surface has to resolve.
   */
  externalId: z.string().nullable().optional(),
  /** Where the subscriber is — address, a contact line, GPS — as SmartOLT records it per ONU. */
  address: z.string().nullable().optional(),
  contact: z.string().nullable().optional(),
  latitude: z.number().nullable().optional(),
  longitude: z.number().nullable().optional(),
  lastSeenAt: z.number().int().nullable(),
  /**
   * The chassis's own record of the last transition — when the ONU last came
   * up, when it last went down and why, read over SNMP where the generation
   * carries it (ZTE zxAnGponSrvOnu status table on the C300 and C6xx; Huawei
   * from its ONT control table). Absent where the chassis has no such record.
   */
  lastOnlineAt: z.number().int().nullable().optional(),
  lastOfflineAt: z.number().int().nullable().optional(),
  lastOfflineReason: onuDownReasonSchema.nullable().optional(),
  /**
   * The chassis's verdict on the ONU's configuration; ZTE only so far — and
   * null for a foreign ONT on a ZTE chassis, where the verdict is on the
   * chassis's proprietary extras rather than on the service
   * (`chassisConfigVerdictApplies`, 2026-09-21).
   */
  configState: onuConfigStateSchema.nullable().optional(),
  /**
   * Derived, not a chassis state: the ONU has been down longer than the operator
   * threshold (`dormantAfterDays`, default 30) — a subscriber who likely left.
   * The API computes it from `state` + `lastSeenAt`; the UI reads it through
   * {@link onuHealthLabel}/{@link onuHealthTone} to set it apart from a fresh drop.
   */
  dormant: z.boolean().optional(),
  /**
   * The deferred half of a resync pushed while this ONU was offline, or the
   * standing record of one. `waiting` — pushed to the OLT, awaiting the unit's
   * return to confirm it took; `failed` — 24h passed without it returning, so it
   * needs an explicit resync. Null when nothing is outstanding. See
   * pending-confirm.ts.
   */
  pendingConfirmation: z
    .object({
      status: z.enum(['waiting', 'failed']),
      kind: z.string(),
      queuedAt: z.number().int(),
      expiresAt: z.number().int(),
      message: z.string().nullable(),
    })
    .nullable()
    .optional(),
  /**
   * WiFi set on this ONU while it was offline: `waiting` until it informs the
   * ACS and the push lands, then a brief `applied`. Read from its own findings
   * store (wifi-pending.ts), resolved by the CPE inform — separate from the
   * resync pending-confirm above.
   */
  wifiPending: z
    .object({
      status: z.enum(['waiting', 'applied']),
      subject: z.string(),
      summary: z.string(),
      createdAt: z.number().int(),
    })
    .nullable()
    .optional(),
  /**
   * The recorded WAN mode, queued for a CPE that has JUST been linked to this
   * ONU (a first link, or the replacement after a Swap): `waiting` until the
   * CPE informs and the push lands — or is found to match already — then a
   * brief `applied`. Same store and lifecycle as `wifiPending` (wan-pending.ts).
   */
  wanPending: z
    .object({
      status: z.enum(['waiting', 'applied']),
      subject: z.string(),
      summary: z.string(),
      createdAt: z.number().int(),
    })
    .nullable()
    .optional(),
  /**
   * When this ONU last came up, and whether that is exact.
   *
   * Derived from `onu_events` rather than read from the chassis: it costs no
   * command on a live fleet, it is the same on both vendors, and it agrees
   * with the Events tab because it IS the Events tab.
   *
   * Null means the platform cannot say — the ONU is down, or nothing was ever
   * recorded for it. `atLeast` means the run began at or before the earliest
   * event we hold, so the true uptime is longer than this and rendering a bare
   * duration would be inventing precision.
   */
  uptime: z
    .object({ since: z.number().int(), atLeast: z.boolean() })
    .nullable()
    .optional(),
  authorizedAt: z.number().int().nullable(),
  /**
   * When this platform first recorded the ONU — authorised here, or adopted
   * off the chassis by a sweep or an import. Only an authorise through the
   * platform (or a description that carries the date) gives `authorizedAt`;
   * an adopted unit has none, so this is what "newest first" falls back to.
   */
  createdAt: z.number().int().optional(),
  /**
   * When a sweep last found the chassis no longer configuring this ONU.
   * Null is the normal case. A row is kept rather than deleted because its
   * optical history and state changes are the record of a subscriber who
   * existed, and a swapped ONU that comes back clears this by itself.
   */
  removedAt: z.number().int().nullable().optional(),
  /**
   * Set while a chassis write for this ONU is still running, and `failed` when
   * one did not land. Null is the normal, settled case.
   *
   * An authorise returns as soon as the row exists and drives the chassis
   * behind it, so the page an operator lands on has to be able to say which of
   * the three it is looking at.
   */
  provisioningStatus: z.enum(['running', 'failed']).nullable().optional(),
  provisioningMessage: z.string().nullable().optional(),
  /**
   * How the operator last asked for this ONU to be managed, and when.
   *
   * Intent, which is not the same as state: whether an ONU IS on TR-069 is
   * answered by `deviceId`. Between the write and the first inform there is a
   * gap of up to one inform interval where the two disagree, and this is what
   * lets the page say "waiting" rather than "OMCI" — or notice the reverse, an
   * ONU set to OMCI that is still informing.
   */
  managementMode: z.enum(['tr069', 'omci']).nullable().optional(),
  managementSetAt: z.number().int().nullable().optional(),
  /** Taken out of service by a person: never matched by a bulk run, never written to. */
  disabledAt: z.number().int().nullable().optional(),
  disabledReason: z.string().nullable().optional(),
  /**
   * How this ONU is managed IN FACT — three states, not two.
   *
   * `managementMode` above is an intent; this is derived from the ACS the
   * chassis reports against the ACS servers this ONU's own organisation runs.
   * `tr069_other` is the state that did not exist before and needed to: an ONU
   * configured for TR-069 and pointed somewhere else looks managed and is not,
   * and only OMCI reaches it.
   *
   * Optional, because a row no chassis read has touched yet cannot honestly
   * claim any of the three.
   */
  managementState: onuManagementStateSchema.optional(),
  /**
   * When this ONU's CPE last informed the ACS.
   *
   * A DIFFERENT fact from `lastSeenAt`, which the OLT sweep advances only when
   * it reads the ONU as `working` — so a unit whose chassis has not been swept
   * lately reads as last seen hours ago while it is informing every few
   * minutes. That is the exact complaint this answers. Null where the ONU has
   * no TR-069 device, which is most of a PON fleet.
   */
  lastInformAt: z.number().int().nullable().optional(),
  /**
   * Whether an ONU the chassis says is on OUR ACS is actually reaching it.
   *
   * Computed by the SERVER, because it depends on the operator's own online
   * window (Settings → Timings) and the browser has no business holding a
   * second copy of that number. Null where the question does not apply — OMCI,
   * somebody else's ACS, or a chassis not yet read. See `tr069Reachability`.
   */
  tr069Reach: z.enum(['silent', 'stale', 'informing']).nullable().optional(),
  /**
   * The subscriber WAN as the linked CPE last reported it (2026-09-20):
   * `up` — a routed subscriber connection holding an address; `no_address` —
   * a routed subscriber connection with NO address (RADIUS rejecting its MAC,
   * a DHCP server not answering, PPPoE refused: the customer is off, and
   * only the router log said so for 18 gachie1 subscribers); `bridged` —
   * the subscriber connection hands the VLAN to the house router and has no
   * address of its own; `none` — the CPE reports no subscriber connection at
   * all; `unknown` — no linked CPE, or one the ACS has not read. Read off
   * `devices.wan_state`, refreshed by the device sweep.
   */
  wanState: z.enum(['up', 'no_address', 'bridged', 'none', 'unknown']).nullable().optional(),
  /**
   * The OLT and the ACS disagree about this ONU.
   *
   * True when a CPE is informing OUR ACS right now — so the ONU is on our
   * TR-069 in fact — while the OLT's own config carries no ACS line, so a
   * chassis read classifies it OMCI. A bridged subscriber informs over its WAN
   * rather than the OLT path, which is exactly how a real TR-069 subscriber ends
   * up reading OMCI on the fleet list. Flagged rather than silently relabelled
   * so the OLT config can be corrected — see `oltAcsMismatch` and the
   * `tr069_olt_mismatch` finding. Always present; false is the normal case.
   */
  oltAcsMismatch: z.boolean().optional(),
  /** What the chassis last reported, and when. Null until a read has happened. */
  acsUrl: z.string().nullable().optional(),
  acsUsername: z.string().nullable().optional(),
  acsReadAt: z.number().int().nullable().optional(),
  updatedAt: z.number().int(),
});
export type Onu = z.infer<typeof onuSchema>;

/**
 * How an ONU is managed. `mode` is the OMCI↔TR-069 switch: choosing `tr069`
 * provisions the ONU's TR-069 client (over the OLT) to point at an ACS, so it
 * appears in that ACS (and, for our local GenieACS, is auto-claimed); `omci`
 * keeps it OLT/OMCI-managed only. `addressing` is how the ONU's management
 * interface gets its IP. The ACS URL and credentials are NOT chosen here — they
 * come from the ONU's resolved TR-069 profile; the operator only picks the mode,
 * addressing, and (optionally) which associated profile via `tr069ProfileId` on
 * the request. The platform never exposes GenieACS to operators directly.
 */
export const onuManagementSchema = z.object({
  mode: z.enum(['omci', 'tr069']),
  addressing: z.enum(['dhcp', 'static']),
  /** Management VLAN the TR-069 traffic is tagged with. */
  mgmtVlan: z.number().int().min(1).max(4094).nullable(),
  /** Required only when addressing is static. */
  staticIp: z.string().ip().nullable().optional(),
  staticMask: z.string().ip().nullable().optional(),
  staticGateway: z.string().ip().nullable().optional(),
});
export type OnuManagement = z.infer<typeof onuManagementSchema>;

/**
 * The OLT-side management context for a TR-069 device, resolved by joining the
 * device to its ONU row. `linked` is false when the device is not (yet) joined
 * to an OLT — the management card can't act without an OLT-side identity. When
 * linked, `profiles` is the OLT's associated TR-069 set (with its default) that
 * the profile selector is constrained to, and `currentProfileId` is the profile
 * in effect for this ONU right now.
 */
export const onuManagementContextSchema = z.object({
  linked: z.boolean(),
  olt: z
    .object({ id: z.string(), name: z.string(), vendor: z.enum(['zte', 'huawei']) })
    .nullable(),
  onu: z
    .object({
      id: z.string(),
      serial: z.string(),
      ponPort: z.string(),
      onuIndex: z.string(),
      state: z.string(),
      tr069ProfileId: z.string().nullable(),
      managedExternally: z.boolean(),
      /** True once the ONU has informed this ACS. False means OMCI-managed. */
      deviceLinked: z.boolean(),
    })
    .nullable(),
  profiles: z.array(oltTr069ProfileSchema),
  currentProfileId: z.string().nullable(),
});
export type OnuManagementContext = z.infer<typeof onuManagementContextSchema>;

export interface SignalBand {
  /**
   * The bottom of the band, and whether the bottom itself is in it.
   *
   * Both halves are needed because the top band genuinely differs from the
   * rest: an overdriven receiver is `rx > -8`, exclusive, while every band
   * below is `rx >= floor`. Exactly -8 dBm is therefore GOOD, not too hot —
   * a distinction a table of bare numbers cannot express, and getting it
   * wrong moves one boundary reading into the wrong colour.
   */
  readonly floor: number;
  readonly floorInclusive: boolean;
  /** What the ONU list's pill says. */
  readonly label: string;
  readonly tone: 'success' | 'warn' | 'danger';
  /** The same reading as triage sees it — see `signalLevel`. */
  readonly level: 'high' | 'normal' | 'low';
}

/**
 * What the receiver can physically do — the bottom of the light budget.
 *
 * GPON Class B+ optics are specified to about −28 dBm. Below it the ONU is not
 * weak, it is past what the part is rated for, and the link stops being a
 * question of signal quality at all.
 *
 * It is named because everything else at the bottom of the scale is DERIVED
 * from it: the critical threshold sits 1 dB above it, the ONU page's meter
 * draws its scale down to it, and both used to carry the number themselves —
 * the meter as a literal, the threshold as a comment explaining a literal. Fit
 * Class C+ optics one day (−30) and this is the single line that moves.
 */
export const RECEIVER_SENSITIVITY_DBM = -28;

/**
 * At or below this, an ONU is at the edge of what its receiver can do.
 *
 * One dB above the floor, which is the whole of its reasoning: close enough to
 * the limit to be worth a callout, far enough to still be carrying traffic.
 * It is BOTH the degradation report's trigger and the fair/poor boundary of
 * the band table below — the same number meaning the same thing, which it was
 * not before. `optics.ts` declared its own copy and tested `<= -27`, while the
 * table gave `fair` an inclusive floor of -27, so a subscriber reading exactly
 * -27.0 got an amber "fair" pill and a report saying they were past the limit.
 */
export const CRITICAL_DBM = RECEIVER_SENSITIVITY_DBM + 1;

/** Above this the receiver is overdriven — exclusive, so -8 itself is healthy. */
const TOO_HOT_FLOOR_DBM = -8;
/** At or above this the light is simply good. */
const GOOD_FLOOR_DBM = -25;

/**
 * The optical bands, in one place, ordered from most light to least.
 *
 * THIS IS THE SOURCE. `signalBand` and `signalLevel` both read it,
 * `summarizeOpticalPower` is built on `signalLevel`, and the degradation
 * report's `CRITICAL_DBM` is the floor of the `fair` band above — so the ONU
 * list's coloured pill, the signal filter, the power card and the report
 * cannot disagree about where a boundary is. That was already the stated rule
 * — `summarizeOpticalPower`'s own comment says three copies of a number like
 * -8 dBm is how a card ends up reporting twelve problems the list cannot show
 * — but the rule was kept by hand, in separate ladders of if-statements.
 *
 * This constant existed alongside them and was read by nothing, which is the
 * only reason it went unnoticed that it said something DIFFERENT: it carried a
 * `max` per band, and under that reading -8 was the ceiling of "too hot"
 * rather than its floor — the exact inversion. Anything that had started
 * consuming it would have disagreed with every pill on the platform. It is now
 * the table the functions actually use, so the two cannot drift again.
 *
 * `poor` therefore means exactly "at or past the critical limit", and `fair`
 * stops one step above it. There is deliberately no fifth band below
 * `RECEIVER_SENSITIVITY_DBM`: nothing on the fleet reads below it, the weakest
 * subscriber measured -27.96 dBm, and the meter already shows how close to the
 * floor a reading is. A colour operators must learn for a case that does not
 * occur is a cost with no reader.
 */
export const SIGNAL_BANDS: readonly SignalBand[] = [
  { floor: TOO_HOT_FLOOR_DBM, floorInclusive: false, label: 'too hot', tone: 'warn', level: 'high' },
  { floor: GOOD_FLOOR_DBM, floorInclusive: true, label: 'good', tone: 'success', level: 'normal' },
  // Exclusive: -27 itself is POOR, because that is what the report calls it.
  { floor: CRITICAL_DBM, floorInclusive: false, label: 'fair', tone: 'warn', level: 'low' },
  {
    floor: Number.NEGATIVE_INFINITY,
    floorInclusive: true,
    label: 'poor',
    tone: 'danger',
    level: 'low',
  },
];

/**
 * The two boundaries triage is drawn at, as plain numbers.
 *
 * For the one caller that cannot run `signalLevel`: the bell counts wrong
 * light in SQL, over the whole fleet, without loading a row into JavaScript.
 * Its comment already claimed it used "the SAME thresholds the list's coloured
 * pill and its optical card use" — and that was true only by coincidence,
 * because it had -8 and -25 typed into the query. Now it is true by
 * construction.
 *
 * `high` is EXCLUSIVE (a receiver is overdriven above it, and -8 itself is a
 * healthy reading); `low` is exclusive downward for the same reason from the
 * other side, since -25 belongs to the normal band. Anything reading these has
 * to keep that asymmetry, which is why they are named rather than exported as
 * an array of bare numbers.
 */
export const SIGNAL_LEVEL_BOUNDARIES = {
  /** Above this, exclusive: too much light. */
  high: SIGNAL_BANDS.find((b) => b.level === 'high')!.floor,
  /** Below this, exclusive: too little. */
  low: SIGNAL_BANDS.find((b) => b.level === 'normal')!.floor,
} as const;

/**
 * The band a reading falls in — the first whose floor it clears.
 *
 * The final band's floor is -Infinity, so every real number lands somewhere;
 * the fallback is for NaN, which clears no floor at all and has always read as
 * `poor` here rather than as a missing measurement.
 */
function bandFor(rxPowerDbm: number): SignalBand {
  const found = SIGNAL_BANDS.find((band) =>
    band.floorInclusive ? rxPowerDbm >= band.floor : rxPowerDbm > band.floor,
  );
  return found ?? SIGNAL_BANDS[SIGNAL_BANDS.length - 1]!;
}

export function signalBand(rxPowerDbm: number | null | undefined): {
  label: string;
  tone: 'success' | 'warn' | 'danger' | 'neutral';
} {
  if (rxPowerDbm === null || rxPowerDbm === undefined) return { label: 'unknown', tone: 'neutral' };
  const band = bandFor(rxPowerDbm);
  return { label: band.label, tone: band.tone };
}

/**
 * The same reading as `signalBand`, said the way an operator triages.
 *
 * Three answers, not four: is the light too strong, about right, or too weak.
 * `signalBand` splits weak into fair and poor because that difference matters
 * when you are looking at one subscriber; it does not matter when you are
 * filtering a list for the ones worth visiting.
 */
export function signalLevel(
  rxPowerDbm: number | null | undefined,
): 'high' | 'normal' | 'low' | 'unknown' {
  if (rxPowerDbm === null || rxPowerDbm === undefined) return 'unknown';
  // Read off SIGNAL_BANDS rather than from a second ladder of thresholds. The
  // four-way band collapses to three here because fair and poor are both `low`:
  // above -8 dBm the receiver is being overdriven, which damages a link as
  // surely as too little light and is the one people forget to look for.
  return bandFor(rxPowerDbm).level;
}

/**
 * How a set of ONUs is doing on light, counted the way an operator triages.
 *
 * Built on `signalLevel` rather than on its own thresholds, deliberately: the
 * ONU list's coloured pill, its signal filter and this summary have to agree
 * about what "too hot" means, and three copies of a number like -8 dBm is how
 * a card ends up reporting twelve problems the list cannot show.
 *
 * `unknown` is counted and never folded into a healthy number. An ONU nothing
 * has measured is not an ONU that is fine — on a fleet where a chassis read
 * can fail quietly, "42 not measured" is itself the finding.
 *
 * The extremes come back with the counts because they are what makes the
 * number actionable: "9 too hot" is a filter, "9 too hot, worst -3.1 dBm" is
 * a decision about whether to drive out today.
 */
export interface OpticalPowerSummary {
  /** Over -8 dBm: the receiver is being overdriven. */
  high: number;
  /** Under -25 dBm: too little light. */
  low: number;
  normal: number;
  /** Nothing has measured this one. Not a healthy state — an unknown one. */
  unknown: number;
  /** Rows considered, including the unmeasured ones. */
  total: number;
  /** The strongest reading seen, when anything was measured. */
  highestDbm: number | null;
  /** The weakest reading seen, when anything was measured. */
  lowestDbm: number | null;
}

export function summarizeOpticalPower(
  rows: ReadonlyArray<{ rxPowerDbm: number | null }>,
): OpticalPowerSummary {
  const out: OpticalPowerSummary = {
    high: 0,
    low: 0,
    normal: 0,
    unknown: 0,
    total: rows.length,
    highestDbm: null,
    lowestDbm: null,
  };
  for (const row of rows) {
    const level = signalLevel(row.rxPowerDbm);
    out[level] += 1;
    const dbm = row.rxPowerDbm;
    if (dbm === null || dbm === undefined) continue;
    out.highestDbm = out.highestDbm === null ? dbm : Math.max(out.highestDbm, dbm);
    out.lowestDbm = out.lowestDbm === null ? dbm : Math.min(out.lowestDbm, dbm);
  }
  return out;
}

/** A line card: interfaces + `show card` inventory + ENTITY-MIB detail. */
export const oltCardSchema = z.object({
  shelf: z.number().int(),
  slot: z.number().int(),
  kind: z.enum(['pon', 'uplink', 'other']),
  portCount: z.number().int(),
  portsUp: z.number().int(),
  /** Configured card type from `show card` (GTGH, HUVQ…) or ENTITY-MIB. */
  model: z.string().nullable(),
  serial: z.string().nullable(),
  name: z.string().nullable(),
  /** INSERVICE / STANDBY / OFFLINE from `show card`, when the CLI is reachable. */
  status: z.string().nullable(),
  /** Per-card load over SNMP where the chassis exposes it (the ZTE C6xx); absent elsewhere. */
  cpuPercent: z.number().nullable().optional(),
  memoryPercent: z.number().nullable().optional(),
});
export type OltCard = z.infer<typeof oltCardSchema>;

export const cardActionSchema = z.object({
  shelf: z.coerce.number().int().min(0),
  slot: z.coerce.number().int().min(0),
  action: z.enum(['restart-onus']),
  dryRun: z.boolean().default(true),
});
export type CardActionRequest = z.infer<typeof cardActionSchema>;

/** Platform-side ONU limit applied to every PON port on one card. */
export const cardLimitSchema = z.object({
  shelf: z.coerce.number().int().min(0),
  slot: z.coerce.number().int().min(0),
  maxOnus: z.coerce.number().int().min(1).max(256).nullable(),
});
export type CardLimitRequest = z.infer<typeof cardLimitSchema>;

/**
 * A VLAN the OLT carries.
 *
 * Two kinds of fact live here and the UI keeps them apart. The VLAN's
 * existence and its name are chassis configuration, read by the sweep and
 * changed only through a CLI write. The rest — what the VLAN is for, and which
 * PON ports should default to it — is SimpleACS classification: it drives what
 * the platform offers when provisioning an ONU, and writes nothing to the OLT.
 */
export const oltVlanSchema = z.object({
  id: z.string(),
  oltId: z.string(),
  vlanId: z.number().int(),
  /** The VLAN name the chassis reports — usually a restatement of the id. */
  name: z.string().nullable(),
  /** What the operator calls it. Stored here; the OLT is never told. */
  description: z.string().nullable(),
  usedForIptv: z.boolean(),
  usedForMgmt: z.boolean(),
  /**
   * A management VLAN's subnet — the one its ONUs take their addresses from,
   * which the gateway must route for the ACS to reach them. Recorded by the
   * onboarding wizard (2026-09-25); null on every other VLAN.
   */
  subnet: z.string().nullable().optional(),
  dhcpSnooping: z.boolean(),
  lanToLan: z.boolean(),
  /** Explicitly on or off for subscriber use; null follows `vlanSelectableForOnus`. */
  onuSelectable: z.boolean().nullable().optional(),
  defaultPonPorts: z.array(z.string()),
  /** `discovered` was read off the chassis; `manual` has not been seen there yet. */
  source: z.enum(['discovered', 'manual']),
  /** ONUs SimpleACS has provisioned onto this VLAN. */
  onuCount: z.number().int(),
  /**
   * ONUs the chassis itself says are on it, from the last read. Null when
   * never read. This is the honest figure on a fleet SimpleACS did not
   * provision, where `onuCount` is correctly zero and useless.
   */
  chassisOnuCount: z.number().int().nullable().optional(),
  chassisOnuCountAt: z.number().int().nullable().optional(),
  /** Uplink ports carrying it. Empty means it cannot reach the network. */
  uplinks: z.array(z.string()),
  createdAt: z.number().int(),
  updatedAt: z.number().int(),
});
export type OltVlan = z.infer<typeof oltVlanSchema>;

/**
 * What `GET /olts/:id/vlans` answers.
 *
 * A page of VLAN rows plus the one thing a picker cannot work out from them:
 * whether the org that owns this chassis offers VLAN 1. They travel together
 * so a browser can never hold a fresh list against a stale policy — see the
 * route's own note.
 */
export interface OltVlanListResponse {
  items: OltVlan[];
  total: number;
  limit: number;
  offset: number;
  vlan1Selectable: boolean;
}

/** The three columns any VLAN row has to carry to be judged at all. */
export interface VlanOfferCandidate {
  vlanId: number;
  usedForMgmt?: boolean;
  /** Explicitly on or off for subscriber use; null/absent means "use the rule". */
  onuSelectable?: boolean | null;
}

/**
 * What a picker knows BESIDES the VLAN row, and cannot decide without.
 *
 * `vlan1Enabled` is the tenant's standing answer, recorded on the organization
 * and defaulting to off. It comes from the org that owns the CHASSIS, never
 * from whoever is signed in — a superadmin looking at another tenant's OLT is
 * asking about that tenant's fleet, not their own.
 *
 * `alreadyOn` is the ONU in front of you: the VLANs it is carrying right now.
 * It is the whole difference between OFFERING VLAN 1 as a new choice and
 * REFUSING TO WORK WITH a subscriber who is already on it. On this fleet 190
 * of gachie1's 193 authorised ONUs carry VLAN 1, so a rule with no
 * `alreadyOn` in it takes their WAN editor away on the day it deploys — and a
 * picker that cannot represent the value it is editing is broken besides.
 */
export interface VlanOfferPolicy {
  vlan1Enabled?: boolean;
  alreadyOn?: readonly number[];
}

/**
 * Whether a VLAN may be attached to a subscriber's ONU.
 *
 * The order of these four tests is the whole rule, so it is worth reading as
 * one:
 *
 * 1. A management VLAN, never. It is the platform's own path to the ONU —
 *    subscriber traffic on it means their traffic and the chassis's are the
 *    same traffic, and the first sign is usually that management stops
 *    working. There is a separate management picker fed from exactly this
 *    flag, so nothing is left unrepresentable by refusing it here.
 * 2. A VLAN this ONU is already on, always. See `alreadyOn` above.
 * 3. The chassis's own classification, where an operator set one. A VLAN
 *    ticked (or un-ticked) on the OLT's VLANs tab is a deliberate act about
 *    THAT chassis and outranks any fleet-wide default in either direction —
 *    including an operator who turned VLAN 1 on for one OLT before this org
 *    setting existed, whose choice would otherwise be revoked by a default.
 * 4. Otherwise VLAN 1 follows the org setting, and everything else is offered.
 *    VLAN 1 is every switch's untagged default and carries whatever nobody
 *    classified; a subscriber on it is indistinguishable from one never
 *    configured, which is a mistake most networks make exactly once.
 */
export function vlanSelectableForOnus(
  vlan: VlanOfferCandidate,
  policy?: VlanOfferPolicy,
): boolean {
  if (vlan.usedForMgmt) return false;
  if (policy?.alreadyOn?.includes(vlan.vlanId)) return true;
  if (vlan.onuSelectable !== null && vlan.onuSelectable !== undefined) return vlan.onuSelectable;
  if (vlan.vlanId === 1) return policy?.vlan1Enabled === true;
  return true;
}

/** A chassis's VLAN list, reduced to what one ONU may be offered. */
export interface VlanOfferList<T> {
  /** What the picker shows, in the order it was given. */
  offered: T[];
  /**
   * This chassis carries VLAN 1, and it is being withheld by the org setting
   * rather than absent.
   *
   * Reported so a picker can SAY so. "VLAN 1 is not in the list" and "VLAN 1
   * is not on this OLT" look identical to an operator, and the first has an
   * answer one checkbox away.
   */
  vlan1Withheld: boolean;
}

/**
 * The candidate list, decided in one place from everything it needs.
 *
 * A caller filtering the list itself has to remember to pass the policy, and
 * the one that forgets silently offers VLAN 1 to a tenant that switched it off
 * — or, worse, withholds it from the ONU already riding it. Handing the whole
 * chassis list and the whole policy to one function is what makes that
 * impossible rather than merely discouraged.
 */
export function offerVlansToOnu<T extends VlanOfferCandidate>(
  vlans: readonly T[],
  policy?: VlanOfferPolicy,
): VlanOfferList<T> {
  const offered = vlans.filter((v) => vlanSelectableForOnus(v, policy));
  return {
    offered,
    vlan1Withheld:
      vlans.some((v) => v.vlanId === 1) && !offered.some((v) => v.vlanId === 1),
  };
}

/** Everything SimpleACS records about a VLAN — never sent to the chassis. */
export const oltVlanClassificationSchema = z.object({
  description: z.string().max(64).nullable().optional(),
  usedForIptv: z.boolean().optional(),
  usedForMgmt: z.boolean().optional(),
  dhcpSnooping: z.boolean().optional(),
  lanToLan: z.boolean().optional(),
  onuSelectable: z.boolean().nullable().optional(),
  defaultPonPorts: z.array(z.string().max(32)).max(256).optional(),
});
export type OltVlanClassification = z.infer<typeof oltVlanClassificationSchema>;

/** Creating a VLAN writes to the OLT, so it follows the dry-run discipline. */
export const createOltVlanSchema = oltVlanClassificationSchema.extend({
  vlanId: z.coerce.number().int().min(1).max(4094),
  dryRun: z.boolean().default(true),
});
export type CreateOltVlanRequest = z.infer<typeof createOltVlanSchema>;

/** "50-60" or "50,51,80" — how an operator asks for a batch. */
export const createOltVlansSchema = oltVlanClassificationSchema.extend({
  vlans: z.string().min(1).max(200),
  /** Each VLAN is described as prefix + its id, e.g. `cvlan_` -> `cvlan_51`. */
  descriptionPrefix: z.string().max(48).nullable().optional(),
  dryRun: z.boolean().default(true),
});
export type CreateOltVlansRequest = z.infer<typeof createOltVlansSchema>;

export const deleteOltVlansSchema = z.object({
  vlanIds: z.array(z.coerce.number().int().min(1).max(4094)).min(1).max(256),
  dryRun: z.boolean().default(true),
});
export type DeleteOltVlansRequest = z.infer<typeof deleteOltVlansSchema>;

/** Expands "50-60,80" into [50..60, 80]. Throws nothing — bad parts are dropped. */
export function expandVlanRanges(input: string): number[] {
  const out = new Set<number>();
  for (const part of input.split(',')) {
    const range = /^\s*(\d+)\s*-\s*(\d+)\s*$/.exec(part);
    if (range) {
      const from = Number(range[1]);
      const to = Number(range[2]);
      if (from >= 1 && to <= 4094 && from <= to) {
        for (let v = from; v <= to; v += 1) out.add(v);
      }
      continue;
    }
    const one = Number(part.trim());
    if (Number.isInteger(one) && one >= 1 && one <= 4094) out.add(one);
  }
  return [...out].sort((a, b) => a - b);
}

/** Compact per-uplink VLAN state, read from the running config in one session. */
export const uplinkVlanSummarySchema = z.object({
  ifName: z.string(),
  mode: z.string().nullable(),
  pvid: z.number().int().nullable(),
  taggedVlans: z.array(z.number().int()),
  untaggedVlans: z.array(z.number().int()),
});
export type UplinkVlanSummary = z.infer<typeof uplinkVlanSummarySchema>;

/**
 * How a VLAN reads in every picker: `80 - cvlan_u2`, or just `80` when nobody
 * has named it. One function so the OLT tab, the ONU VLAN list, the management
 * VLAN selector and the port editors cannot drift apart.
 */
export function vlanLabel(vlan: {
  vlanId: number;
  description?: string | null;
  name?: string | null;
}): string {
  const text = vlan.description?.trim() || vlan.name?.trim();
  return text ? `${vlan.vlanId} - ${text}` : String(vlan.vlanId);
}

/** "1,12-14,22-100" — how an operator reads a VLAN list. */
export function compressVlanRanges(vlans: number[]): string {
  const sorted = [...new Set(vlans)].sort((a, b) => a - b);
  const parts: string[] = [];
  for (let i = 0; i < sorted.length; ) {
    let j = i;
    while (j + 1 < sorted.length && sorted[j + 1]! === sorted[j]! + 1) j += 1;
    parts.push(j > i ? `${sorted[i]}-${sorted[j]}` : `${sorted[i]}`);
    i = j + 1;
  }
  return parts.join(',');
}

export const uplinkPortSchema = z.object({
  ifIndex: z.number().int(),
  name: z.string(),
  description: z.string().nullable(),
  adminStatus: z.enum(['enabled', 'disabled', 'unknown']),
  operStatus: z.enum(['up', 'down', 'unknown']),
  speedMbps: z.number().int().nullable(),
  pvid: z.number().int().nullable(),
  taggedVlans: z.array(z.number().int()),
  untaggedVlans: z.array(z.number().int()),
});
export type UplinkPort = z.infer<typeof uplinkPortSchema>;

/** Platform-side PON port bookkeeping: the label and the provisioning limit. */
export const patchPonPortSchema = z.object({
  description: z.string().max(200).nullable().optional(),
  maxOnus: z.coerce.number().int().min(1).max(256).nullable().optional(),
});
export type PatchPonPortRequest = z.infer<typeof patchPonPortSchema>;

export const ponPortActionSchema = z.object({
  action: z.enum(['enable', 'disable', 'restart-onus']),
  /** true returns the exact CLI without touching the OLT. */
  dryRun: z.boolean().default(true),
});
export type PonPortActionRequest = z.infer<typeof ponPortActionSchema>;

/** Uplink names carry slashes (`gei_1/19/1`), so they travel in the body. */
export const uplinkActionSchema = z.object({
  ifName: z.string().min(1).max(64),
  action: z.enum(['enable', 'disable']),
  dryRun: z.boolean().default(true),
});
export type UplinkActionRequest = z.infer<typeof uplinkActionSchema>;

export const uplinkVlansSchema = z.object({
  ifName: z.string().min(1).max(64),
  pvid: z.coerce.number().int().min(1).max(4094).optional(),
  addTagged: z.array(z.coerce.number().int().min(1).max(4094)).default([]),
  removeTagged: z.array(z.coerce.number().int().min(1).max(4094)).default([]),
  dryRun: z.boolean().default(true),
});
export type UplinkVlansRequest = z.infer<typeof uplinkVlansSchema>;

/**
 * Outcome of a CLI-backed action. The commands are always returned so the
 * operator sees exactly what runs; the transcript is present only after a
 * real execution and must be read — ZTE reports errors inline, not by exit
 * status.
 */
/**
 * WHICH writer takes an ONU's subscriber WAN on `POST …/omci-wan` — answered
 * on the dry run and the live call both, so a surface that shows the plan
 * (the WAN card's disclosure, the dialog's Review) reads it off the answer
 * instead of assuming the chassis `wan-ip` line for every ZTE unit. That
 * assumption is what the 2026-09-14 duplicate-WAN incident was made of:
 * `wan-ip 1 mode dhcp` on RTEGC6A2955E, whose WAN is the CPE's own TR-069-made
 * `Internet_DHCP`, stood a SECOND routed WAN up beside it.
 *
 *   huawei_ont   a Huawei ONT, whatever chassis: `configureWan` over TR-069
 *   zte_tr069    a ZTE ONU whose CPE terminates the WAN itself (a routed
 *                subscriber connection on the VLAN that the OMCI did not
 *                make): `configureWan` over TR-069, no chassis `wan-ip`
 *   zte_chassis  the OMCI `wan-ip` line — an unlinked ZTE ONU, or one whose
 *                connection on the VLAN is the OMCI-made `omci_*`
 *   huawei_chassis  a Huawei OLT's own OMCI WAN (`ont ipconfig … ip-index 1`,
 *                `ont internet-config`) — only when the WAN's owner is the
 *                OLT, and sent marked UNVERIFIED until a Huawei OLT has run it
 */
export const subscriberWanWriterSchema = z.enum(['huawei_ont', 'zte_tr069', 'zte_chassis', 'huawei_chassis']);
export type SubscriberWanWriter = z.infer<typeof subscriberWanWriterSchema>;

export const cliActionResultSchema = z.object({
  commands: z.array(z.string()),
  executed: z.boolean(),
  transcript: z.string().nullable(),
  warnings: z.array(z.string()),
  /**
   * Resync dry run only. True when the ONU's chassis-side interface has no
   * T-CONT/GEM behind a vport and the plan could not fill one — the refusal
   * sentence is in `warnings`, `commands` is empty, and re-running with
   * `upstreamProfileId` (an UPSTREAM speed profile) gives the interface its
   * T-CONTs from that profile before the resync. Absent otherwise.
   */
  needsUpstreamProfile: z.boolean().optional(),
  /**
   * Resync dry run only. The upstream speed profile the plan filled a bare
   * interface's T-CONTs from when the caller chose none — the org's highest
   * (the operator's default). Present so the client can offer to change it;
   * absent when every vport already had a T-CONT or the caller chose.
   */
  upstreamProfileDefault: z.string().optional(),
  /**
   * omci-wan only. The writer the plan chose for this ONU (see
   * `subscriberWanWriterSchema`) and, in `writerReason`, the one sentence
   * why — "over TR-069 on WANIPConnection.5 (Internet_DHCP) — the CPE
   * terminates this WAN itself". Absent on every other action.
   */
  writer: subscriberWanWriterSchema.optional(),
  writerReason: z.string().optional(),
});
export type CliActionResult = z.infer<typeof cliActionResultSchema>;

/**
 * VoIP on one ONU, switched from the chassis.
 *
 * `POST /olts/:id/onus/:onuId/voip`. Dry-run by default, like every other
 * chassis write. `potsPorts` names the ONU's POTS sockets (1-based, the way
 * the ZTE grammar spells them — `pots_0/1`); absent means every port the
 * model has. `profile` is the chassis-side SIP profile name; without it the
 * protocol is switched and no port is bound (the C300 takes both forms).
 * `protocol` exists so a future `h248`/`mgcp` can be named; only SIP is
 * offered now, because only SIP has been captured.
 */
export const onuVoipSchema = z.object({
  enabled: z.boolean(),
  protocol: z.enum(['sip']).default('sip'),
  potsPorts: z.array(z.number().int().min(1).max(8)).max(8).optional(),
  profile: z.string().trim().min(1).max(32).optional(),
  dryRun: z.boolean().default(true),
});
export type OnuVoipInput = z.infer<typeof onuVoipSchema>;

/** What `GET …/voip` reads back: the recorded intent plus what gates it. */
export const onuVoipStateSchema = z.object({
  /** The last intent recorded here; null = never set from this platform. */
  enabled: z.boolean().nullable(),
  /** POTS sockets the ONU's type is known to have; null = the library does not say. */
  potsPorts: z.number().int().nullable(),
  /** The sentence the write would refuse with on this chassis/unit, or null where it runs. */
  refusal: z.string().nullable(),
  /**
   * Where it runs but has never been run on this chassis family (Huawei,
   * since 2026-09-25): the sentence saying so, which the write also carries
   * first in its warnings. Null where it is verified or refused.
   */
  unverified: z.string().nullable(),
});
export type OnuVoipState = z.infer<typeof onuVoipStateSchema>;

/**
 * IPTV (multicast) on one ONU, switched from the chassis.
 *
 * `POST /olts/:id/onus/:onuId/iptv`. `vlanId` must be a VLAN the OLT carries
 * (its VLAN table); absent, the one VLAN the table marks as IPTV. `ethPorts`
 * are the Ethernet sockets the multicast VLAN is stripped onto (1-based);
 * absent means every socket the model has.
 */
export const onuIptvSchema = z.object({
  enabled: z.boolean(),
  vlanId: z.number().int().min(1).max(4094).optional(),
  ethPorts: z.array(z.number().int().min(1).max(16)).max(16).optional(),
  /**
   * Huawei only: the VLAN of the subscriber service-port the multicast
   * membership hangs off, when the ONT carries more than one outside the
   * management VLANs. Absent, the one subscriber service-port is picked (and
   * a choice between several is refused, naming this field). A ZTE unit
   * carries IPTV on its own `mvlan` lines and does not use it.
   */
  servicePortVlan: z.number().int().min(1).max(4094).optional(),
  dryRun: z.boolean().default(true),
});
export type OnuIptvInput = z.infer<typeof onuIptvSchema>;

/** What `GET …/iptv` reads back. */
export const onuIptvStateSchema = z.object({
  enabled: z.boolean().nullable(),
  /** The multicast VLAN last written, null when never set. */
  vlanId: z.number().int().nullable(),
  /** The OLT's VLANs marked as IPTV — the picker's real objects. */
  oltIptvVlans: z.array(z.number().int()),
  /** Ethernet sockets the ONU's type is known to have; null = the library does not say (a write then covers four). */
  ethPorts: z.number().int().nullable(),
  refusal: z.string().nullable(),
  /** As on `onuVoipStateSchema`: sent but never yet run on this chassis family, or null. */
  unverified: z.string().nullable(),
});
export type OnuIptvState = z.infer<typeof onuIptvStateSchema>;

/**
 * Which figure a throughput point's rate is.
 *
 * `counters`: the difference of the lifetime byte totals between this point
 * and the previous one, over the seconds between them — exact for that
 * interval, so a burst shows. `chassis`: the rate line the chassis prints
 * (the ZTE C300's "Input/Output rate"), which is ITS OWN moving average over a
 * long window — a one-second 150 Mbit/s line test read as ~2 Mbit/s there
 * (2026-09-13) — used only when no delta can be taken (a first point, a
 * previous one too old, a counter that restarted).
 */
export const onuTrafficBasisSchema = z.enum(['counters', 'chassis']);
export type OnuTrafficBasis = z.infer<typeof onuTrafficBasisSchema>;

/**
 * One point on an ONU's throughput series.
 *
 * `down`/`up` are the SUBSCRIBER's directions and Bps is bytes per second:
 * since 2026-09-13 the rate over the interval since the previous point, taken
 * from the lifetime byte totals (`basis: counters`), because the chassis's own
 * rate line averages over so long a window that a line test never showed on
 * it; that line is what a point falls back to when there is no previous point
 * to pair with (`basis: chassis`). `downBytes`/`upBytes` are the lifetime
 * counters at that instant, for a client that would rather draw its own
 * average between points. A null figure is one the chassis did not give (an
 * offline ONU, a vendor with no counter), never a zero measured. `basis` is
 * carried on the point a sample request just took; stored points read back
 * without it (the table keeps no column for it).
 */
export const onuTrafficSampleSchema = z.object({
  at: z.number().int(),
  downBps: z.number().nullable(),
  upBps: z.number().nullable(),
  downBytes: z.number().nullable(),
  upBytes: z.number().nullable(),
  basis: onuTrafficBasisSchema.optional(),
});
export type OnuTrafficSample = z.infer<typeof onuTrafficSampleSchema>;

/** What `GET …/traffic` reads back: the stored series and how it is collected. */
export const onuTrafficSeriesSchema = z.object({
  samples: z.array(onuTrafficSampleSchema),
  /** The sentence a fresh sample would refuse with on this chassis/unit, or null where it reads. */
  refusal: z.string().nullable(),
  /** How the rate is read on this ONU's chassis: `cli` (ZTE) or `snmp` (Huawei). */
  source: z.enum(['cli', 'snmp']).nullable(),
});
export type OnuTrafficSeries = z.infer<typeof onuTrafficSeriesSchema>;

/**
 * The chassis half of an ONU delete that could not be done at the time: the
 * record is already gone from SimpleACS, and this is the `no onu` / `ont
 * delete` still owed to the OLT, retried for up to 48 hours.
 */
export const deferredDeauthorizationStatusSchema = z.enum(['pending', 'done', 'expired', 'cancelled']);
export type DeferredDeauthorizationStatus = z.infer<typeof deferredDeauthorizationStatusSchema>;

export const deferredDeauthorizationSchema = z.object({
  id: z.string(),
  oltId: z.string(),
  ponPort: z.string(),
  onuIndex: z.string(),
  serial: z.string(),
  vendor: z.string(),
  /** The exact list that will run, as rendered when the ONU was deleted. */
  commands: z.array(z.string()),
  attempts: z.number().int(),
  /** The last chassis answer or failure; null once it has landed. */
  lastError: z.string().nullable(),
  lastAttemptAt: z.number().nullable(),
  createdAt: z.number(),
  /** createdAt + 48h; a row still pending past this is expired and raised on the bell. */
  expiresAt: z.number(),
  status: deferredDeauthorizationStatusSchema,
  doneAt: z.number().nullable(),
  /** Who asked for the delete. */
  actor: z.string().nullable(),
});
export type DeferredDeauthorization = z.infer<typeof deferredDeauthorizationSchema>;

/** A stored copy of the OLT's running config. */
export const oltBackupKindSchema = z.enum(['daily', 'weekly', 'monthly', 'manual']);
export type OltBackupKind = z.infer<typeof oltBackupKindSchema>;

export const oltBackupSchema = z.object({
  id: z.string(),
  oltId: z.string(),
  kind: oltBackupKindSchema,
  sizeBytes: z.number().int(),
  note: z.string().nullable(),
  createdBy: z.string().nullable(),
  takenAt: z.number().int(),
  /** Present only on the single-backup endpoint. */
  content: z.string().optional(),
});
export type OltBackup = z.infer<typeof oltBackupSchema>;

export const createBackupSchema = z.object({
  note: z.string().max(200).nullable().optional(),
});
export type CreateBackupRequest = z.infer<typeof createBackupSchema>;

/**
 * Seed the ONU-type library from ONE chosen backup of one OLT.
 *
 * Additive by contract: a model already in the library is HELD — never
 * overwritten, whatever its source — and a new one arrives with the backup
 * named as its provenance. Dry-run by default, like every write that deserves
 * a preview.
 */
export const seedOnuTypesSchema = z.object({
  backupId: z.string().min(1),
  dryRun: z.boolean().default(true),
});
export type SeedOnuTypesRequest = z.infer<typeof seedOnuTypesSchema>;

/** One model the backup declares, and what the seed did (or would do) with it. */
export const seededOnuModelSchema = z.object({
  name: z.string(),
  action: z.enum(['created', 'held']),
  vendor: z.string().nullable(),
  ponType: z.string(),
  ethPorts: z.number().int().nullable(),
  wifiSsids: z.number().int().nullable(),
  voipPorts: z.number().int().nullable(),
  catv: z.number().int().nullable(),
});
export type SeededOnuModel = z.infer<typeof seededOnuModelSchema>;

export const seedOnuTypesResultSchema = z.object({
  dryRun: z.boolean(),
  /** Which backup was read — the same identity the provenance note carries. */
  backup: z.object({ id: z.string(), kind: oltBackupKindSchema, takenAt: z.number().int() }),
  created: z.number().int(),
  held: z.number().int(),
  models: z.array(seededOnuModelSchema),
  warnings: z.array(z.string()),
});
export type SeedOnuTypesResult = z.infer<typeof seedOnuTypesResultSchema>;

/** Commit-confirm restore job state. */
export const restorePhaseSchema = z.enum([
  'applying',
  'verifying',
  'awaiting-olt',
  'confirmed',
  'failed',
]);
export const restoreJobSchema = z.object({
  oltId: z.string(),
  backupId: z.string(),
  startedBy: z.string(),
  phase: restorePhaseSchema,
  linesTotal: z.number().int(),
  linesApplied: z.number().int(),
  errors: z.array(z.string()),
  message: z.string(),
  startedAt: z.number().int(),
  updatedAt: z.number().int(),
});
export type RestoreJob = z.infer<typeof restoreJobSchema>;

/** An operational report row (nightly drift checks, restores, …). */
export const reportSchema = z.object({
  id: z.string(),
  orgId: z.string().nullable(),
  kind: z.string(),
  oltId: z.string().nullable(),
  deviceId: z.string().nullable(),
  /** Present on per-ONU findings, so the UI can link and act without parsing. */
  onuId: z.string().nullable().optional(),
  subject: z.string(),
  summary: z.string(),
  detail: z.unknown().nullable(),
  status: z.enum(['open', 'fixed', 'fix_failed', 'dismissed']),
  createdAt: z.number().int(),
});
export type Report = z.infer<typeof reportSchema>;

/** Decrypted access secrets, returned only by the audited reveal endpoint. */
export const oltCredentialsSchema = z.object({
  snmpCommunity: z.string().nullable(),
  snmpCommunityRw: z.string().nullable(),
  snmpV3Username: z.string().nullable(),
  cliUsername: z.string().nullable(),
  cliPassword: z.string().nullable(),
});
export type OltCredentials = z.infer<typeof oltCredentialsSchema>;

/** One item of the full functional health check on the Diagnostics tab. */
export const oltHealthCheckSchema = z.object({
  id: z.string(),
  label: z.string(),
  /** null = skipped (e.g. no CLI credentials, unverified vendor). */
  ok: z.boolean().nullable(),
  /** Optional checks (SNMP write) do not fail the overall verdict. */
  optional: z.boolean(),
  latencyMs: z.number().int().nullable(),
  detail: z.string().nullable(),
  hint: z.string().nullable(),
});
export type OltHealthCheck = z.infer<typeof oltHealthCheckSchema>;

export const oltHealthSchema = z.object({
  ok: z.boolean(),
  checks: z.array(oltHealthCheckSchema),
  /** When this result was produced. Absent only on legacy in-flight results. */
  checkedAt: z.number().int().optional(),
});
export type OltHealth = z.infer<typeof oltHealthSchema>;

/** Stored last health state; null until the first scheduled or manual run. */
export const oltHealthSnapshotSchema = z.object({
  latest: oltHealthSchema.nullable(),
});
export type OltHealthSnapshot = z.infer<typeof oltHealthSnapshotSchema>;

/**
 * One ONU state transition as `GET /api/olts/:id/events` lists it — the answer
 * to "why was this subscriber down?", kept in the chassis's own distinction
 * (dying gasp is a power cut, LOS is a fibre cut) rather than flattened to
 * "offline". The serial is denormalised onto the event so the history outlives
 * the ONU row; `name` is joined from the row and is null once it is gone.
 * `description` is the operator-facing sentence for `cause`, written by the
 * API so every reader shows the same words.
 */
export const onuEventKindSchema = z.enum(['up', 'down', 'changed']);
export type OnuEventKind = z.infer<typeof onuEventKindSchema>;

export const onuEventCauseSchema = z.enum([
  'power_fail',
  'fibre_loss',
  'unreachable',
  'restored',
  'unknown',
]);
export type OnuEventCause = z.infer<typeof onuEventCauseSchema>;

export const onuEventSchema = z.object({
  id: z.string(),
  oltId: z.string(),
  onuId: z.string(),
  serial: z.string(),
  /** Subscriber name from the ONU row; null once that row is gone. */
  name: z.string().nullable(),
  interfaceName: z.string().nullable(),
  kind: onuEventKindSchema,
  cause: onuEventCauseSchema,
  description: z.string(),
  /** States as the chassis reported them; stored as text, so not narrowed to the enum. */
  fromState: z.string().nullable(),
  toState: z.string(),
  at: z.number().int(),
});
export type OnuEvent = z.infer<typeof onuEventSchema>;

/** What a connection test actually managed to do. */
export const oltProbeSchema = z.object({
  reachable: z.boolean(),
  snmp: z.object({
    ok: z.boolean(),
    latencyMs: z.number().int().nullable(),
    sysDescr: z.string().nullable(),
    sysName: z.string().nullable(),
    uptimeSeconds: z.number().int().nullable(),
    error: z.string().nullable(),
  }),
  cli: z.object({
    ok: z.boolean(),
    latencyMs: z.number().int().nullable(),
    banner: z.string().nullable(),
    error: z.string().nullable(),
  }),
  detectedVendor: oltVendorSchema.nullable(),
  detectedModel: z.string().nullable(),
});
export type OltProbe = z.infer<typeof oltProbeSchema>;

export const oltSyncResultSchema = z.object({
  oltId: z.string(),
  ponPorts: z.number().int(),
  onus: z.number().int(),
  linkedDevices: z.number().int(),
  durationMs: z.number().int(),
  warnings: z.array(z.string()),
});
export type OltSyncResult = z.infer<typeof oltSyncResultSchema>;

/**
 * What deleting this chassis would take with it — shown in the confirmation
 * dialog BEFORE anything happens, so the operator confirms the real footprint
 * (192 ONUs, 41 backups) rather than an abstract "are you sure".
 *
 * Deleting is a records operation: rows here and CPE records on the ACS.
 * NOTHING is sent to the chassis itself — its running config is untouched.
 */
export const oltDeletePlanSchema = z.object({
  name: z.string(),
  number: z.number().int().nullable(),
  /** ONU rows that will be removed, with their per-ONU children. */
  onus: z.number().int(),
  /** CPE records removed from GenieACS and from the local device list. */
  acsDevices: z.number().int(),
  /** Config backups whose rows AND stored bodies go. */
  backups: z.number().int(),
  /** Field housings are physical and survive — they are DETACHED, not deleted. */
  splitters: z.number().int(),
  /** Open/closed findings about this chassis, moot once it is gone. */
  reports: z.number().int(),
  /** Why the delete would refuse right now (a live bulk run), or null. */
  blockedBy: z.string().nullable(),
});
export type OltDeletePlan = z.infer<typeof oltDeletePlanSchema>;

export const oltDeleteResultSchema = z.object({
  ok: z.literal(true),
  cleared: z.object({
    onus: z.number().int(),
    /** How many CPE records actually left GenieACS — the shortfall is in `warnings`. */
    acsDevices: z.number().int(),
    backups: z.number().int(),
    reports: z.number().int(),
  }),
  detachedSplitters: z.number().int(),
  warnings: z.array(z.string()),
});
export type OltDeleteResult = z.infer<typeof oltDeleteResultSchema>;

/**
 * ONU events on one chassis that the CALLING USER has not looked at yet.
 *
 * Deliberately not a field on `Olt`: that DTO is the same for everybody and is
 * handed to the public status page, while this number is one person's reading
 * history. It is fetched for the whole fleet in one request (GET
 * /olts/unseen-events) and joined to the rows on the client, the same way the
 * auto-authorise windows already are.
 */
export const oltUnseenEventsSchema = z.object({
  oltId: z.string(),
  unseen: z.number().int(),
});
export type OltUnseenEvents = z.infer<typeof oltUnseenEventsSchema>;

/** One row of an SNMP walk, for verifying OID maps against real hardware. */
export const snmpWalkRowSchema = z.object({
  oid: z.string(),
  type: z.string(),
  value: z.string(),
});
export type SnmpWalkRow = z.infer<typeof snmpWalkRowSchema>;

/** One of the per-slot figures a chassis-level reading was summarised from. */
export const oltStatPartSchema = z.object({
  label: z.string(),
  /** Already formatted with its unit, or `no reading` where the slot reports none. */
  value: z.string(),
});
export type OltStatPart = z.infer<typeof oltStatPartSchema>;

/**
 * One line of the chassis Stats panel.
 *
 * A reading always carries either a value with the source that produced it, or
 * a note saying why this chassis does not report it — never silence. The panel
 * asks for five figures and this fleet can only answer some of them, and an
 * operator has to be able to tell "checked, this box does not say" apart from
 * "nobody looked". Dropping the row would lose exactly that distinction.
 */
export const oltStatReadingSchema = z.object({
  /** The figure as it should be shown, or null when the chassis does not report it. */
  value: z.string().nullable(),
  /** What produced the figure — the OID, command or record, so it can be audited. */
  source: z.string().nullable(),
  /** Why it is missing. Always set when `value` is null. */
  note: z.string().nullable(),
  /**
   * The per-board, per-slot or per-module figures the value was summarised
   * from.
   *
   * A chassis reports one temperature per card and the panel shows one per
   * chassis, so a single number is always a choice — the intake sensor, the
   * hottest board, the shelf total. Carrying the whole list makes that choice
   * checkable: a 67 °C card cannot hide behind a 41 °C headline when the card
   * is right there under it. Optional so an older API keeps parsing.
   */
  parts: z.array(oltStatPartSchema).optional(),
});
export type OltStatReading = z.infer<typeof oltStatReadingSchema>;

/**
 * One fan unit, as the chassis reports it live.
 *
 * Both speed fields are nullable and at most one is ever set. A ZTE C300 fills
 * `rpm` (`show fan` prints ActualSpeed(RPM)); a C650 fills `percent` (its
 * `show fan` prints a percentage of full speed and no RPM at all); Huawei
 * fills neither, because no verified command on either Huawei chassis reports
 * a fan speed — `display fan speed priority` answers a strategy, which is not
 * a reading. A unit with no speed still belongs here: `display emu` says the
 * fan is fitted and healthy, and that is worth showing.
 */
export const oltFanUnitSchema = z.object({
  label: z.string(),
  rpm: z.number().nullable(),
  /**
   * Speed as a percentage of full speed — which is how the ZTE C650 reports
   * it, and the C650 reports nothing else.
   *
   * A separate field from `rpm` on purpose. The C300 answers RPM and the C650
   * answers a percentage; there is no conversion between them that is not
   * invented, so each generation fills the field it actually answers and a
   * reader can tell which it is looking at. Optional so an older API keeps
   * parsing.
   */
  percent: z.number().nullable().optional(),
  /** Speed level, health state, board name — whatever the chassis says about it. */
  detail: z.string().nullable(),
});
export type OltFanUnit = z.infer<typeof oltFanUnitSchema>;

/** Uptime, temperature, fans, voltage and power draw for one chassis. */
export const oltStatsSchema = z.object({
  uptime: oltStatReadingSchema,
  temperature: oltStatReadingSchema,
  fans: oltStatReadingSchema.extend({ units: z.array(oltFanUnitSchema) }),
  voltage: oltStatReadingSchema,
  power: oltStatReadingSchema,
  /** The verbatim config lines the fan facts were read from. */
  fanEvidence: z.array(z.string()),
  /** Which stored backup those lines came from, and when it was taken. */
  configSource: z.string().nullable(),
  configTakenAt: z.number().int().nullable(),
  /**
   * When temperature, fans, voltage and power were last read off the chassis.
   *
   * Those four come from a CLI session, so they are served from a stored
   * snapshot and can be minutes old — the Details tab polls every thirty
   * seconds and a chassis login per poll is not a thing to do to a live OLT.
   * The age is part of the reading: a stale figure that says how stale it is
   * is useful, and one that does not is a lie with a timestamp missing.
   * null means no environment read has been taken yet.
   */
  environmentAt: z.number().int().nullable().optional(),
});
export type OltStats = z.infer<typeof oltStatsSchema>;

/**
 * The per-ONU security features a chassis applies on the ONU's interface —
 * DHCP option 82, IP source guard, the MAC-learning limit and the OLT-side
 * HTTP/telnet management ports.
 *
 * `POST /olts/:id/onus/:onuId/security-features`. Any subset: only the parts
 * given are rendered and recorded, so a body of `{ maxMacLearn: 8 }` touches
 * nothing else. Dry-run by default, like every other chassis write.
 *
 * Option 82 and IP source guard bind to the ONU's SUBSCRIBER vport (ZTE) or
 * service-port (Huawei) — the one carrying the internet VLAN, never the
 * management channel — which the route picks from what the chassis was last
 * read as carrying; `vlanId` names the service outright when the ONU carries
 * more than one. `trust` is option 82's relay-agent trust flag and only means
 * anything with `enabled: true`.
 *
 * `maxMacLearn: null` asks to LIFT the limit; the C300 form for that has not
 * been probed, so the route refuses it by name rather than guessing a `no`.
 */
export const onuSecurityFeaturesSchema = z
  .object({
    option82: z.object({ enabled: z.boolean(), trust: z.boolean().optional() }).optional(),
    sourceGuard: z.object({ enabled: z.boolean() }).optional(),
    maxMacLearn: z.number().int().min(0).max(16383).nullable().optional(),
    mgmtPorts: z.object({ http: z.boolean().optional(), telnet: z.boolean().optional() }).optional(),
    vlanId: z.number().int().min(1).max(4094).optional(),
    dryRun: z.boolean().default(true),
  })
  .strict();
export type OnuSecurityFeaturesInput = z.infer<typeof onuSecurityFeaturesSchema>;

/**
 * What `GET …/security-features` reads back: the intent last recorded here
 * (null = never set from this platform; no chassis read reports these) and,
 * per feature, the sentence the write would refuse with on this chassis, or
 * null where it runs — and, where it runs but has never been run on this
 * chassis family (Huawei, since 2026-09-25), the sentence saying so, which
 * the write also carries in its warnings. Null where it is verified or
 * refused.
 */
export const onuSecurityFeaturesStateSchema = z.object({
  option82: z.object({ enabled: z.boolean().nullable() }),
  sourceGuard: z.object({ enabled: z.boolean().nullable() }),
  maxMacLearn: z.number().int().nullable(),
  mgmtPorts: z.object({ http: z.boolean().nullable(), telnet: z.boolean().nullable() }),
  refusals: z.object({
    option82: z.string().nullable(),
    sourceGuard: z.string().nullable(),
    maxMacLearn: z.string().nullable(),
    mgmtPorts: z.string().nullable(),
  }),
  unverified: z.object({
    option82: z.string().nullable(),
    sourceGuard: z.string().nullable(),
    maxMacLearn: z.string().nullable(),
    mgmtPorts: z.string().nullable(),
  }),
});
export type OnuSecurityFeaturesState = z.infer<typeof onuSecurityFeaturesStateSchema>;
